import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';

import { ICourse } from '../CoursesSlice';
import {
  adminCreateSubscriptionPlan,
  adminDeleteSubscriptionPlan,
  adminGetSubscriptionPlans,
  adminGetSubscriptionPlansForDropDown,
  adminUpdateSubscriptionPlan,
  adminVerifySubscriptionPlanDelete,
} from './adminApi';
import { RootState } from '../..';
import { getUserSubscriptionPlans } from './api';

export const SUBSCRIPTION_PLAN_STATUSES = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
} as const;

export const SUBSCRIPTION_PLAN_BILLING_PERIOD_TYPES = {
  DAYS: 'DAYS',
  WEEKS: 'WEEKS',
  MONTHS: 'MONTHS',
  YEARS: 'YEARS',
} as const;

export const SUBSCRIPTION_PLAN_CRITERIAS = {
  ALL: 'ALL',
  INCLUDE: 'INCLUDE',
  EXCLUDE: 'EXCLUDE',
} as const;

export type TSubscriptionPlanStatuses = typeof SUBSCRIPTION_PLAN_STATUSES[keyof typeof SUBSCRIPTION_PLAN_STATUSES];
export type TSubscriptionPlanBillingPeriodTypes = typeof SUBSCRIPTION_PLAN_BILLING_PERIOD_TYPES[keyof typeof SUBSCRIPTION_PLAN_BILLING_PERIOD_TYPES];
export type TSubscriptionPlanCriterias = typeof SUBSCRIPTION_PLAN_CRITERIAS[keyof typeof SUBSCRIPTION_PLAN_CRITERIAS];

export interface ISubscriptionPlan {
  _id: string;
  name: string;
  status: TSubscriptionPlanStatuses;
  features: string[];
  billingPeriod: {
    count: number;
    type: TSubscriptionPlanBillingPeriodTypes;
  };
  courses?: string[] | ICourse[];
  criteria?: TSubscriptionPlanCriterias;
  courseChecked: boolean;
  creditsChecked: boolean;
  isMostPopular: boolean;
  amount?: number;
  currency?: string;
  credits?: number;
}

export interface ISubscriptionPlanDeleteVerificationData {
  studentSubscriptionRateCount: number;
}

export interface IInitialSubscriptionPlansState {
  status: 'idle' | 'loading' | 'rejected';
  userSubscriptionPlans: Array<ISubscriptionPlan>;
  // Admin
  adminSubscriptionPlanListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ISubscriptionPlan>;
  };
  adminSubscriptionPlanListDataForDropDown?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ISubscriptionPlan>;
  };
  adminSubscriptionPlanDeleteVerificationData?: ISubscriptionPlanDeleteVerificationData;
}

const initialState: IInitialSubscriptionPlansState = {
  status: 'idle',
  userSubscriptionPlans: [],
  // Admin
  adminSubscriptionPlanListData: undefined,
  adminSubscriptionPlanListDataForDropDown: undefined,
  adminSubscriptionPlanDeleteVerificationData: undefined,
};

export const SubscriptionPlansSlice = createSlice({
  initialState,
  name: 'subscriptionPlansSlice',
  reducers: {
    resetUserSubscriptionPlans: (state) => {
      state.userSubscriptionPlans = [];
    },
    // Admin
    resetAdminSubscriptionPlanListData: (state) => {
      state.adminSubscriptionPlanListData = undefined;
    },
    resetAdminSubscriptionPlanListDataForDropDown: (state) => {
      state.adminSubscriptionPlanListDataForDropDown = undefined;
    },
    resetAdminSubscriptionPlanDeleteVerificationData: (state) => {
      state.adminSubscriptionPlanDeleteVerificationData = undefined;
    },
  },
  extraReducers(builder) {
    builder.addCase(getUserSubscriptionPlans.fulfilled, (state, action) => {
      const result = action.payload;

      state.userSubscriptionPlans = result || [];
    });

    builder
      // Admin
      .addCase(adminGetSubscriptionPlans.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminSubscriptionPlanListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminSubscriptionPlanListData = newAdminSubscriptionPlanListData;
      })
      .addCase(
        adminGetSubscriptionPlansForDropDown.fulfilled,
        (state, action) => {
          const result = action.payload;

          const newAdminSubscriptionPlanListDataForDropDown = {
            limit: result.limit,
            offset: result.offset,
            totalDocs: result.totalDocs || 0,
            list: result.docs || [],
          };

          state.adminSubscriptionPlanListDataForDropDown = newAdminSubscriptionPlanListDataForDropDown;
        }
      )
      .addCase(adminCreateSubscriptionPlan.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminSubscriptionPlanListData) {
          const newAdminSubscriptionPlanListData = {
            ...state.adminSubscriptionPlanListData,
          };

          newAdminSubscriptionPlanListData.list.unshift(result);

          if (newAdminSubscriptionPlanListData.totalDocs) {
            newAdminSubscriptionPlanListData.totalDocs += 1;
          } else {
            newAdminSubscriptionPlanListData.totalDocs = 1;
          }

          state.adminSubscriptionPlanListData = newAdminSubscriptionPlanListData;
        }
      })
      .addCase(adminUpdateSubscriptionPlan.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminSubscriptionPlanListData &&
          state.adminSubscriptionPlanListData.list &&
          state.adminSubscriptionPlanListData.list.length
        ) {
          const newAdminSubscriptionPlanListData = {
            ...state.adminSubscriptionPlanListData,
          };

          const index = newAdminSubscriptionPlanListData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminSubscriptionPlanListData.list[index] = result;
          }

          state.adminSubscriptionPlanListData = newAdminSubscriptionPlanListData;
        }
      })
      .addCase(adminDeleteSubscriptionPlan.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminSubscriptionPlanListData &&
          state.adminSubscriptionPlanListData.list &&
          state.adminSubscriptionPlanListData.list.length
        ) {
          const newAdminSubscriptionPlanListData = {
            ...state.adminSubscriptionPlanListData,
          };

          newAdminSubscriptionPlanListData.totalDocs = newAdminSubscriptionPlanListData.totalDocs
            ? newAdminSubscriptionPlanListData.totalDocs - 1
            : 0;

          newAdminSubscriptionPlanListData.list = newAdminSubscriptionPlanListData.list.filter(
            (item) => item._id !== result._id
          );

          state.adminSubscriptionPlanListData = newAdminSubscriptionPlanListData;
        }
      })
      .addCase(adminVerifySubscriptionPlanDelete.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminSubscriptionPlanDeleteVerificationData = result;
      });

    // Common Matchers
    builder.addMatcher(
      isPending(
        getUserSubscriptionPlans,
        // Admin
        adminCreateSubscriptionPlan,
        adminGetSubscriptionPlans,
        adminUpdateSubscriptionPlan,
        adminDeleteSubscriptionPlan
      ),
      (state, action) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isFulfilled(
        getUserSubscriptionPlans,
        // Admin
        adminCreateSubscriptionPlan,
        adminGetSubscriptionPlans,
        adminUpdateSubscriptionPlan,
        adminDeleteSubscriptionPlan
      ),
      (state, action) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isRejected(
        getUserSubscriptionPlans,

        // Admin
        adminCreateSubscriptionPlan,
        adminGetSubscriptionPlans,
        adminUpdateSubscriptionPlan,
        adminDeleteSubscriptionPlan
      ),
      (state, action) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getSubscriptionPlansState = (
  state: RootState
): RootState['subscriptionPlans'] => state.subscriptionPlans;

export const {
  resetUserSubscriptionPlans,
  // Admin
  resetAdminSubscriptionPlanListData,
  resetAdminSubscriptionPlanListDataForDropDown,
  resetAdminSubscriptionPlanDeleteVerificationData,
} = SubscriptionPlansSlice.actions;

export * from './adminApi';
export * from './api';
export default SubscriptionPlansSlice.reducer;
